import { useEffect, useState } from 'react';

const isTextInput = (el: Element | null): el is HTMLElement =>
  el instanceof HTMLInputElement ||
  el instanceof HTMLTextAreaElement ||
  (el instanceof HTMLElement && el.isContentEditable);

/**
 * Высота экранной клавиатуры, перекрывающей страницу, в px.
 *
 * Telegram на iOS не ужимает webview под клавиатуру: layout viewport остаётся
 * полным, а видимая область (visualViewport) становится ниже ровно на её
 * высоту. Элементы с `fixed; bottom: 0` при этом уезжают за клавиатуру, и
 * WebKit начинает сам «подтягивать» страницу, чтобы показать поле и каретку —
 * рывками, при каждом изменении высоты клавиатуры и при вводе. На Android и на
 * десктопе webview ужимается целиком, разницы нет — инсет равен нулю.
 *
 * Пока клавиатура открыта, скролл окна держим на нуле: страница у нас не
 * скроллится (body overflow: hidden), но iOS при фокусе поля всё равно
 * сдвигает документ — из-за этого fixed-элементы и прыгают.
 */
export const useKeyboardInset = (enabled = true) => {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (!enabled) return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const update = () => {
      setInset(Math.max(0, Math.round(window.innerHeight - viewport.height)));
    };

    const resetScroll = () => {
      if (!isTextInput(document.activeElement)) return;
      if (window.scrollY !== 0 || viewport.offsetTop !== 0) {
        window.scrollTo(0, 0);
      }
    };

    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', resetScroll);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', resetScroll);

    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', resetScroll);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', resetScroll);
    };
  }, [enabled]);

  return enabled ? inset : 0;
};
